import type { Locale } from "./types";

export type TimestampDetectedType = "seconds" | "milliseconds" | "datetime";
export type TimestampInputUnit = "auto" | "seconds" | "milliseconds";

export interface TimestampConversionResult {
  detectedType: TimestampDetectedType;
  seconds: string;
  milliseconds: string;
  isoUtc: string;
  date: Date;
}

export function convertTimestampInput(
  input: string,
  unit: TimestampInputUnit = "auto"
): TimestampConversionResult {
  const normalized = input.trim();

  if (normalized === "") {
    throw new Error("INVALID_TIMESTAMP");
  }

  const numericValue = Number(normalized);

  if (!/^[-+]?\d+(?:\.\d+)?$/.test(normalized) || !Number.isFinite(numericValue)) {
    if (unit === "auto") {
      const parsedTimestamp = Date.parse(normalized);

      if (Number.isFinite(parsedTimestamp)) {
        return createTimestampConversionResult(parsedTimestamp, "datetime");
      }

      return createTimestampConversionResult(
        parseDateTimeInput(normalized, getResolvedTimeZone()),
        "datetime"
      );
    }

    throw new Error("INVALID_TIMESTAMP");
  }

  if (unit === "seconds") {
    return createTimestampConversionResult(
      Math.trunc(numericValue * 1000),
      "seconds"
    );
  }

  if (unit === "milliseconds") {
    return createTimestampConversionResult(
      Math.trunc(numericValue),
      "milliseconds"
    );
  }

  return createTimestampConversionResult(
    Math.abs(numericValue) >= 1e11
      ? Math.trunc(numericValue)
      : Math.trunc(numericValue * 1000),
    Math.abs(numericValue) >= 1e11 ? "milliseconds" : "seconds"
  );
}

export function convertDateTimeInput(
  input: string,
  timeZone = getResolvedTimeZone()
): TimestampConversionResult {
  return createTimestampConversionResult(
    parseDateTimeInput(input, timeZone),
    "datetime"
  );
}

export function createCurrentTimestampResult(nowMs = Date.now()): TimestampConversionResult {
  return createTimestampConversionResult(nowMs, "milliseconds");
}

export function formatDateTimeLocalInput(date: Date): string {
  return [
    date.getFullYear().toString().padStart(4, "0"),
    "-",
    (date.getMonth() + 1).toString().padStart(2, "0"),
    "-",
    date.getDate().toString().padStart(2, "0"),
    " ",
    date.getHours().toString().padStart(2, "0"),
    ":",
    date.getMinutes().toString().padStart(2, "0"),
    ":",
    date.getSeconds().toString().padStart(2, "0")
  ].join("");
}

export function formatLocalDateTime(date: Date, locale: Locale): string {
  return date.toLocaleString(locale === "zh" ? "zh-CN" : "en-US", {
    hour12: false
  });
}

export function getResolvedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function getTimestampSelectableTimeZones(): string[] {
  const defaults = [
    "UTC",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Europe/London",
    "America/New_York",
    "America/Los_Angeles"
  ];
  const current = getResolvedTimeZone();
  const intlWithSupportedValues = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };

  if (typeof intlWithSupportedValues.supportedValuesOf === "function") {
    return Array.from(
      new Set([current, "UTC", ...intlWithSupportedValues.supportedValuesOf("timeZone")])
    );
  }

  return Array.from(new Set([current, ...defaults]));
}

export function formatDateTimeInTimeZoneInput(date: Date, timeZone: string): string {
  const parts = getTimeZoneDateParts(date, timeZone);

  return [
    parts.year,
    "-",
    parts.month,
    "-",
    parts.day,
    " ",
    parts.hour,
    ":",
    parts.minute,
    ":",
    parts.second
  ].join("");
}

function createTimestampConversionResult(
  timestampMs: number,
  detectedType: TimestampDetectedType
): TimestampConversionResult {
  if (!Number.isFinite(timestampMs)) {
    throw new Error("INVALID_TIMESTAMP");
  }

  const date = new Date(timestampMs);

  if (Number.isNaN(date.getTime())) {
    throw new Error("INVALID_TIMESTAMP");
  }

  return {
    detectedType,
    seconds: Math.trunc(timestampMs / 1000).toString(),
    milliseconds: Math.trunc(timestampMs).toString(),
    isoUtc: date.toISOString(),
    date
  };
}

function parseDateTimeInput(input: string, timeZone: string): number {
  const normalized = input.trim();

  if (normalized === "") {
    throw new Error("INVALID_TIMESTAMP");
  }

  const compact = normalized
    .replaceAll("/", "-")
    .replaceAll(",", " ")
    .replace(/\s+/g, " ")
    .trim();
  const match = compact.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (!match) {
    throw new Error("INVALID_TIMESTAMP");
  }

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText ?? "0");
  const minute = Number(minuteText ?? "0");
  const second = Number(secondText ?? "0");

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    throw new Error("INVALID_TIMESTAMP");
  }

  const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  let candidate = naiveUtcMs;

  for (let index = 0; index < 3; index += 1) {
    const offset = getTimeZoneOffsetMilliseconds(timeZone, new Date(candidate));
    const next = naiveUtcMs - offset;

    if (next === candidate) {
      break;
    }

    candidate = next;
  }

  const parts = getTimeZoneDateParts(new Date(candidate), timeZone);

  if (
    parts.year !== yearText ||
    parts.month !== monthText ||
    parts.day !== dayText ||
    parts.hour !== hour.toString().padStart(2, "0") ||
    parts.minute !== minute.toString().padStart(2, "0") ||
    parts.second !== second.toString().padStart(2, "0")
  ) {
    throw new Error("INVALID_TIMESTAMP");
  }

  return candidate;
}

function getTimeZoneOffsetMilliseconds(timeZone: string, date: Date): number {
  const parts = getTimeZoneDateParts(date, timeZone);
  const utcTimestamp = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return utcTimestamp - date.getTime();
}

function getTimeZoneDateParts(
  date: Date,
  timeZone: string
): Record<"year" | "month" | "day" | "hour" | "minute" | "second", string> {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  });
  const lookup: Partial<
    Record<"year" | "month" | "day" | "hour" | "minute" | "second", string>
  > = {};

  for (const part of formatter.formatToParts(date)) {
    if (
      part.type === "year" ||
      part.type === "month" ||
      part.type === "day" ||
      part.type === "hour" ||
      part.type === "minute" ||
      part.type === "second"
    ) {
      lookup[part.type] = part.value;
    }
  }

  return {
    year: lookup.year ?? "",
    month: lookup.month ?? "",
    day: lookup.day ?? "",
    hour: lookup.hour ?? "",
    minute: lookup.minute ?? "",
    second: lookup.second ?? ""
  };
}
